import { useState, useContext, useEffect } from "react";
import {
  Typography,
  Card,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import UserContext from "../../context/user";
import BulkUploadRules from "./BulkUploadRules";
import BulkUploadTemplate from "../../assets/template/bulk_upload.csv";
import DownloadLOV from "../../assets/template/Search_LOV.csv";
import { BULK_UPLOAD_CONSTANTS, BUTTONS } from "../../Constant/BulkOpsContant";
import HistoryIcon from "@mui/icons-material/History";
import Dropzone from "../dropzone/Dropzone";
import Progress from "../bulk-operations/Progress";
import PredefinedRulesModal from "./PredefinedRulesModal";
import {
  createRecordAwareChunks,
  uploadChunk,
} from "../../service/bulkServices";
import { UPLOAD_RULES_CONSTANTS } from "../../Constant/BulkOpsContant";
import api from "../../service/api";
import "./bulk-upload.css";
import ResultsProcessing from "../bulk-operations/ResultsProcessing";

const BulkUpload = ({}) => {
  const history = useHistory();
  const UserInfo = useContext(UserContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [newRules, setNewRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState(""); // "uploading", "processing"
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [isPredefinedRulesModalOpen, setIsPredefinedRulesModalOpen] =
    useState(false);
  const [countries, setCountries] = useState([]);
  const [vendorCountries, setVendorCountries] = useState([]);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [selectedDownloadCountry, setSelectedDownloadCountry] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countryAuthError, setCountryAuthError] = useState("");

  const getAuthorizedCommercialCountries = () => {
    const candidates = [
      UserInfo?.commercialcountries,
      UserInfo?.commercialCountries,
      UserInfo?.commercialCountry,
    ];

    const resolved = candidates.find(
      (value) => value !== undefined && value !== null,
    );

    if (Array.isArray(resolved)) {
      return resolved;
    }

    if (typeof resolved === "string" && resolved.trim()) {
      return [resolved.trim()];
    }

    return [];
  };

  const isCountryAuthorizedForRole = (country) => {
    if (!country) {
      return true;
    }

    const authorizedCountries = getAuthorizedCommercialCountries();
    if (authorizedCountries.length === 0) {
      return true;
    }

    const normalizedSelectedCountry = String(country).trim().toLowerCase();
    const normalizedAuthorizedCountries = authorizedCountries
      .map((item) =>
        String(item || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);

    if (normalizedAuthorizedCountries.includes("global")) {
      return true;
    }

    return normalizedAuthorizedCountries.includes(normalizedSelectedCountry);
  };
  const fetchVendorCountries = async () => {
    const countryData = await api.getSelectedDCRCountry();
    const vendorPrefixes = [
      "#IQVIA_US",
      "#IQVIA",
      "#Veeva Network",
      "#OPEN-DATA",
    ];

    const processedCountries = countryData.data.map((country) => {
      for (const prefix of vendorPrefixes) {
        if (country.includes(prefix)) {
          return country.replace(prefix, "").trim();
        }
      }
      return country.trim();
    });

    // Remove duplicates and sort alphabetically
    const uniqueCountries = [...new Set(processedCountries)]
      .filter((country) => country.length > 0)
      .sort();

    return uniqueCountries;
  };
  useEffect(() => {
    try {
      const result = fetchVendorCountries();
      result.then((data) => setCountries(data));
    } catch (error) {
      console.error("Error fetching vendor countries:", error);
    }
  }, []);
  const handleClickRules = () => {
    setIsPredefinedRulesModalOpen(true);
  };

  const handleDownloadClick = () => {
    setShowDownloadPopup(true);
  };

  // Proper CSV parsing function to handle quoted values with commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quotes
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === "," && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = "";
        i++;
      } else {
        current += char;
        i++;
      }
    }

    // Add the last field
    result.push(current.trim());
    return result;
  };

  const handleDownloadSubmit = async () => {
    if (selectedDownloadCountry) {
      setDownloadLoading(true);
      try {
        // Fetch the CSV file content
        const response = await fetch(DownloadLOV);
        const csvText = await response.text();

        // Parse CSV content using proper CSV parser
        const lines = csvText.split("\n");
        const headers = parseCSVLine(lines[0]);

        // Find the country column index
        const countryColumnIndex = headers.findIndex((header) =>
          header.toLowerCase().includes("country"),
        );

        if (countryColumnIndex === -1) {
          setErrorMessage("Country column not found in CSV file.");
          setShowErrorPopup(true);
          setDownloadLoading(false);
          return;
        }

        // Filter data based on selected country
        const filteredData = [];
        filteredData.push(headers); // Add headers first

        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const row = parseCSVLine(lines[i]);
            if (
              row[countryColumnIndex] &&
              row[countryColumnIndex]
                .toLowerCase()
                .includes(selectedDownloadCountry.toLowerCase())
            ) {
              filteredData.push(row);
            }
          }
        }

        // Convert to Excel format and download
        if (filteredData.length > 1) {
          downloadAsExcel(
            filteredData,
            `${selectedDownloadCountry}_LOV_Data.xlsx`,
          );
          setShowDownloadPopup(false);
          setSelectedDownloadCountry("");
        } else {
          setErrorMessage(
            `No data found for country: ${selectedDownloadCountry}`,
          );
          setShowErrorPopup(true);
        }
      } catch (error) {
        console.error("Error processing download:", error);
        setErrorMessage("Error processing download. Please try again.");
        setShowErrorPopup(true);
      } finally {
        setDownloadLoading(false);
      }
    }
  };

  const downloadAsExcel = (data, filename) => {
    // Create Excel-compatible CSV content with proper quoting
    const csvContent = data
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || "");
            // Quote the cell if it contains comma, quote, or newline
            if (
              cellStr.includes(",") ||
              cellStr.includes('"') ||
              cellStr.includes("\n")
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      )
      .join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename.replace(".xlsx", ".csv"));
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCancel = () => {
    setShowDownloadPopup(false);
    setSelectedDownloadCountry("");
  };

  const handleErrorClose = () => {
    setShowErrorPopup(false);
    setErrorMessage("");
  };

  // Function to detect and convert file to UTF-8 encoding
  const convertToUTF8 = async (file) => {
    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const arrayBuffer = e.target.result;
            const uint8Array = new Uint8Array(arrayBuffer);

            // Try to decode as UTF-8 first
            let text;
            try {
              const decoder = new TextDecoder("utf-8", { fatal: true });
              text = decoder.decode(uint8Array);
            } catch (utfError) {
              // If UTF-8 fails, try common encodings
              const encodings = ["windows-1252", "iso-8859-1", "utf-16"];
              let decoded = false;

              for (const encoding of encodings) {
                try {
                  const decoder = new TextDecoder(encoding);
                  text = decoder.decode(uint8Array);
                  decoded = true;
                  console.log(`File decoded using ${encoding} encoding`);
                  break;
                } catch (err) {
                  continue;
                }
              }

              if (!decoded) {
                // Fallback to UTF-8 with replacement characters
                const decoder = new TextDecoder("utf-8", { fatal: false });
                text = decoder.decode(uint8Array);
              }
            }

            // Create a new UTF-8 encoded file
            const utf8Blob = new Blob([text], {
              type: "text/csv;charset=utf-8",
            });

            // Create a File object with the same name and lastModified
            const utf8File = new File([utf8Blob], file.name, {
              type: "text/csv;charset=utf-8",
              lastModified: file.lastModified,
            });

            resolve(utf8File);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      console.error("Error converting file to UTF-8:", error);
      return file; // Return original file if conversion fails
    }
  };

  const handleClickUpload = async () => {
    if (!selectedFile) {
      return;
    }

    // Function to count CSV rows
    const getCsvRowCount = async (file) => {
      try {
        const csvContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsText(file);
        });

        const lines = csvContent.split("\n");
        const dataRows = lines.slice(1).filter((line) => line.trim() !== "");
        return dataRows.length;
      } catch (error) {
        console.error("Error counting CSV rows:", error);
        return 0;
      }
    };
    setLoading(true);
    setShowLoadingDialog(true);
    setUploadPhase("uploading");
    setUploadProgress({ current: 0, total: 0 });

    try {
      // Convert file to UTF-8 encoding before processing
      const utf8File = await convertToUTF8(selectedFile);
      const csvRowCount = await getCsvRowCount(utf8File);
      const chunks = await createRecordAwareChunks(
        utf8File,
        "bulkupload",
        "null",
        "",
        "",
      );
      const totalChunks = chunks.length;
      setUploadProgress({ current: 0, total: totalChunks });
      let uniqueID;
      let createdAt;
      for (let i = 0; i < chunks.length; i++) {
        const chunkData = chunks[i];
        const getData = await uploadChunk(
          chunkData,
          i,
          totalChunks,
          newRules,
          utf8File,
          uniqueID,
          "BULKUPLOAD",
          createdAt,
          "null",
          chunks[i].lastRowNo,
          chunks[i].firstRowNo,
          "null",
          selectedCountry,
        );
        if (i === 0) {
          uniqueID = getData.uploadId;
          createdAt = getData.createdAt;
          sessionStorage.setItem("uploadId", uniqueID);
          sessionStorage.setItem("totalRowUploaded", csvRowCount);
          sessionStorage.setItem("userSelectedCountry", selectedCountry);
        }
        const progressCurrent = i + 1;
        setUploadProgress({ current: progressCurrent, total: totalChunks });
        if (i < chunks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setUploadPhase("processing");
      setUploadProgress({ current: 0, total: 0 });
      setTimeout(() => {
        setShowLoadingDialog(false);
        setLoading(false);
        setSelectedFile(null);
        setUploadPhase("");
        setUploadProgress({ current: 0, total: 0 });
        history.push("/bulk-upload/search-results", {
          uploadId: uniqueID,
          fileName: selectedFile.name,
          uploadSuccess: true,
          createdAt: createdAt,
          uploadType: "bulk-upload",
        });
      }, 20000); // 20 seconds processing simulation
    } catch (error) {
      setError(error.message || "An error occurred during upload");
      setLoading(false);
      setShowLoadingDialog(false);
      setUploadPhase("");
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="container body-height extra-margin-top-less mb-4">
      <Card sx={{ maxWidth: 890 }} className="mx-auto">
        <div className="m-5">
          <Box className="d-flex justify-content-between align-items-center mb-4">
            <Typography variant="h5" className="fw-bold _wg-dynamic-text">
              {BULK_UPLOAD_CONSTANTS.HEADING}
            </Typography>
            <Button
              variant="text"
              startIcon={<HistoryIcon />}
              size="small"
              color="error"
              onClick={() =>
                history.push("/upload-history", { defaultTab: "BULKUPLOAD" })
              }
            >
              {BUTTONS.VIEW_HISTORY}
            </Button>
          </Box>
          <Typography variant="body1" paragraph className="_wg-dynamic-text">
            {BULK_UPLOAD_CONSTANTS.SUB_HEADING}{" "}
          </Typography>
          <Alert
            severity="info"
            onClick={handleClickRules}
            sx={{
              borderRadius: "10px",
              border: "0.667px solid #D1D5DC",
              background: "#FFF",
              "& .MuiAlert-icon": { color: "#000" },
              cursor: "pointer",
            }}
          >
            {UPLOAD_RULES_CONSTANTS.DEFAULT_RULES_INFO}
          </Alert>
          {countries?.length > 0 && (
            <div className="country-div-bulk-upload">
              <label className="country-label-cs">
                Select Country <span className="required-country-cs">*</span>
              </label>
              <Autocomplete
                className="bulk-upload-country-cs"
                disablePortal
                id="combo-box-demo"
                options={countries}
                sx={{ mt: 2 }}
                onChange={(event, newValue) => {
                  const nextCountry = newValue || "";
                  setSelectedCountry(nextCountry);

                  if (!nextCountry) {
                    setCountryAuthError("");
                    return;
                  }

                  if (!isCountryAuthorizedForRole(nextCountry)) {
                    setCountryAuthError(
                      "The selected country is not authorised for your role",
                    );
                  } else {
                    setCountryAuthError("");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Country" />
                )}
              />
              {countryAuthError && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "#d32f2f", fontWeight: 500 }}
                >
                  {countryAuthError}
                </Typography>
              )}
            </div>
          )}
          {/* {UserInfo?.isUserApprover && UserInfo?.dcrSourceOverride && (
            <BulkUploadRules newRules={newRules} setNewRules={setNewRules} />
          )} */}
          {/* upload-container1 replace to upload-container for css */}
          <div className="upload-container1 mt-4">
            <Typography variant="h6" className="mt-4 mb-2">
              {BULK_UPLOAD_CONSTANTS.UPLOAD_HEADING}
            </Typography>
            <Dropzone
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              error={error}
              setError={setError}
              templateUrl={BulkUploadTemplate}
              allowedFormats=".csv"
              typeUpload="bulk-upload"
              searchType=""
              searchIn=""
              handleDownloadClick={handleDownloadClick}
            />
          </div>
          <div className="text-end">
            <Button
              variant="contained"
              color="error"
              className="_wg-dynamic-text mt-4 upload-btn rounded-md"
              disabled={
                !selectedFile ||
                error ||
                loading ||
                !selectedCountry ||
                !!countryAuthError
              }
              onClick={handleClickUpload}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "#ffff" }} />
              ) : (
                BULK_UPLOAD_CONSTANTS.SUBMIT_BUTTON
              )}
            </Button>
          </div>
        </div>
      </Card>
      {/* Loading Dialog */}
      <Dialog
        open={showLoadingDialog}
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogContent
          className="upload-loading-dialog"
          sx={{ py: 4, textAlign: "center" }}
        >
          {uploadPhase === "uploading" && (
            <Progress
              open={showLoadingDialog}
              uploadProgress={uploadProgress}
            />
          )}
          {uploadPhase === "processing" && <ResultsProcessing />}
        </DialogContent>
      </Dialog>
      {isPredefinedRulesModalOpen && (
        <PredefinedRulesModal
          open={isPredefinedRulesModalOpen}
          onClose={() => setIsPredefinedRulesModalOpen(false)}
        />
      )}

      {/* Download Template Popup */}
      <Dialog
        open={showDownloadPopup}
        onClose={handleDownloadCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ color: "#D31710", fontWeight: "bold" }}
          style={{ fontSize: "18px" }}
        >
          Download Reference LOVs
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={{ mt: 2 }}
            options={countries}
            value={selectedDownloadCountry || null}
            onChange={(event, newValue) => {
              setSelectedDownloadCountry(newValue || "");
            }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Country"
                placeholder="Search countries..."
                variant="outlined"
              />
            )}
            noOptionsText="No countries found"
            clearOnEscape
            filterSelectedOptions={false}
            includeInputInList
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDownloadCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDownloadSubmit}
            variant="contained"
            color="error"
            disabled={!selectedDownloadCountry || downloadLoading}
          >
            {downloadLoading ? (
              <CircularProgress size={20} style={{ color: "#fff" }} />
            ) : (
              "Download"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Popup */}
      <Dialog
        open={showErrorPopup}
        onClose={handleErrorClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: "bold" }}>
          Error
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleErrorClose} variant="contained" color="error">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BulkUpload;
